import React from 'react';
import bootbox from 'bootbox';
import moment from 'jalali-moment';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';
import { num } from '../../utils/easy';
import { getCurrentUser } from '../../utils/auth';
import confirm from '../../utils/confirm';
import Dialog from '../../toolkit/dialog';
import ConfirmRemove from './ConfirmRemove';
import Logger from '../../toolkit/logger/Logger';
import UserService from '../../services/user-service';
import DeviceService from '../../services/device-service';
import Pagination from '../widgets/pagination/Pagination';
import TableHeader, { HeaderProps } from './TableHeader';
import DataTable, { DataTableState } from '../widgets/data-table/DataTable';

export interface State extends DataTableState {
  devices: any[];
  userAccess: string;
}

class DevicesTable extends DataTable<RouteComponentProps, State> {

  private userService = new UserService();
  private deviceService = new DeviceService();
  private isUnknownPage: boolean;

  private headOptions: HeaderProps = {
    items: [
      { text: '#', style: { width: '60px', textAlign: 'center' } },
      { text: 'سریال', key: '_id' },
      { text: 'کاربر', key: 'user.name' },
      { text: 'ورژن', key: 'version' },
      { text: 'اعتبار سیمکارت', key: 'credit' },
      { text: 'آخرین زمان ثبت شده', key: 'log.dateTime' },
      { text: 'فعالیت ها' }
    ]
  }

  state = {
    ...this.state,
    devices: [],
    userAccess: '',
    sort: {
      key: 'log.dateTime',
      type: 'desc'
    }
  }

  constructor(props: RouteComponentProps) {
    super(props);
    this.state.devices = [];
    this.isUnknownPage = props.match.path === '/dashboard/unknown-devices';
  }

  render() {
    const state = this.state;
    const { current, limit } = state.pagination;
    let index = ((current - 1) * limit) + 1;
    const searchJsx = (
      <div className="col-md-6 col-12 mx-auto">
        <div className="input-search-component ltr" style={{ marginBottom: '1rem' }}>
          <input
            style={{ textAlign: 'center' }}
            type="text"
            className="input-app input-md placeholder-rtl"
            value={state.search}
            placeholder="میان دستگاه‌ها جستجو کنید..."
            onChange={this.onSearchTextChange} />
        </div>
      </div>
    );
    if (this.isUnknownPage && state.devices.length === 0) {
      return (
        <>
          {searchJsx}
          <div className="text-center">
            <div className="alert alert-primary w-100">
              {(state.search.trim().length === 0) ?
                ('تمامی دستگاه ها شناخته شده هستند') :
                ('دستگاهی با این کلیدواژه یافت نشد')}
            </div>
          </div>
        </>
      );
    }
    return (
      <>
        {searchJsx}
        <table className="table table-striped table-bordered text-center">
          <tbody style={{ width: '100%', display: 'table', background: '#fff' }}>

            <TableHeader items={this.headOptions.items} onSortChange={this.onSortTableChange} />
            {this.state.devices.map(device => (
              <tr key={index}>
                <td className="text-center">{index++}</td>
                <td className="ltr">{device['_id']}</td>
                <td>{this.getDeviceOwner(device)}</td>
                <td>{device['version'] ?? 'ثبت نشده'}</td>
                <td>{device['credit'] ?? 'ثبت نشده'}</td>
                <td className="ltr">{this.getTime(device)}
                </td>
                <td>
                  <div className="actions-icons-list">
                    <Link to={'/dashboard/graph/' + device['_id']} style={{ marginTop: '5px' }}>
                      <i className="fal fa-chart-bar fa-lg ml-2" style={{ color: '#27ae60', marginTop: '5px' }}></i>
                    </Link>
                    {this.renderChangeUserAction(device['_id'])}
                    <Link to={'/dashboard/devices/edit/' + device['_id']}>
                      <i className="fal fa-edit fa-lg ml-2" style={{ color: '#f39c12', marginTop: '4px' }}></i>
                    </Link>
                    {this.renderUnassignAction(device['_id'])}
                    {this.renderRemoveAction(device['_id'])}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          searchKey="q"
          searchValue={state.search}
          current={num(current, 1)}
          max={num(state.pagination.max, 1)}
          onChange={this.onPaginationChange} />
      </>
    );
  }

  componentDidMount = async () => {
    await super.componentDidMount();
    const { access } = getCurrentUser();
    this.setState({ userAccess: access });
  }

  protected fetchData = async () => {
    Logger.info('DevicesTable::fetchData called');
    const service = this.deviceService;
    const { search, pagination, sort } = this.state;
    try {
      const response = await service.pureFetch({
        q: search,
        count: pagination.limit,
        page: pagination.current,
        unknown: this.isUnknownPage ? 1 : 0,
        sort: sort.key,
        sortOrder: sort.type
      });
      if (response.status !== 200) return Logger.error(response);
      const headers = response.headers;
      this.setState({
        devices: response.data,
        pagination: {
          max: num(headers['pagination-count'], null),
          current: num(headers['pagination-page']),
          limit: num(headers['pagination-limit']),
        }
      }, () => Logger.info('DevicesTable::fetchData State Updated'));
    }
    catch (e) {
      Logger.error(e);
    }
  }

  private getDeviceOwner(device: any) {
    if (device.user && device.user.name) {
      return device.user.name;
    }
    return 'نامشخص';
  }

  private getTime = (device: any) => {
    if (device['log'] == null) {
      return 'ثبت نشده';
    }
    const date = moment.from(device['log']['dateTime'], 'en');
    const month = Number(date.format('MM'));
    const addHour = (month <= 6) ? 4 : 3;
    const timeSpan = addHour * 60 + 30;
    return date.subtract(timeSpan, 'minutes').format('YYYY-MM-jDD HH:mm:ss');
  }

  private renderUnassignAction = (deviceId: string) => {
    if (this.isUnknownPage) {
      return '';
    }
    const { userAccess } = this.state;
    if (userAccess !== 'admin' && userAccess !== 'company') {
      return '';
    }
    return (
      <i
        onClick={() => { this.onUnassignUserClicked(deviceId) }}
        title="حذف دسترسی"
        className="fal fa-user-times fa-lg ml-2"
        style={{ color: '#c0392b' }}></i>
    );
  }

  private renderRemoveAction = (deviceId: string) => {
    const { userAccess } = this.state;
    if (userAccess !== 'admin' && userAccess !== 'company') {
      return '';
    }
    return (
      <i
        style={{ color: '#e74c3c' }}
        onClick={this.onRemoveClicked.bind(this, deviceId)}
        className="fal fa-trash-alt fa-lg"></i>
    );
  }

  private renderChangeUserAction = (deviceId: string) => {
    if (!this.isUnknownPage) {
      return '';
    }
    return (
      <i
        title="انتخاب کاربر"
        onClick={() => { this.onChangeUserClicked(deviceId) }}
        className="fal fa-user fa-lg ml-2"
        style={{ color: '#3498db' }}
      ></i>
    );
  }

  private onChangeUserClicked = async (deviceId: string) => {
    const customers = await this.userService.listCustomers();
    this.showChooseCustomerDialog(deviceId, customers);
  }

  private onUnassignUserClicked = async (deviceId: string) => {
    const dialog = confirm({
      title: 'آیا میخواهید دسترسی کاربر را از روی این دستگاه حذف کنید؟',
      type: 'alert',
      primaryClass: 'ant-btn-danger',
      onSubmit: async () => {
        await this.deviceService.unAssignUser(deviceId);
        this.componentDidMount();
      }
    });
    dialog.show();
  }

  private onRemoveClicked = async (deviceId: string) => {
    new Dialog({
      rtl: true,
      showTitle: false,
      size: 'small',
      message: ConfirmRemove,
      onResolve: async (hard) => {
        try {
          await this.deviceService.remove(deviceId, hard);
          this.componentDidMount();
        }
        catch (e) {
          console.error(e);
        }
      },
    }).show()
  }

  private showChooseCustomerDialog = async (deviceId: string, customers: any[]) => {
    let itemsHtml = '';
    for (const customer of customers) {
      itemsHtml += `<li class="customer-item" user-id=${customer._id}>${customer.name}</li>`;
    }
    bootbox.dialog({
      title: 'کاربر این دستگاه را انتخاب کنید',
      message: `
        <div class="user-select-container">
          <ul>${itemsHtml}</ul>
        </div>
      `,
      size: 'large'
    });
    const items = document.querySelectorAll('.customer-item');
    items.forEach(item => {
      item.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const name = target.textContent;
        const userId = target.getAttribute('user-id');
        bootbox.hideAll();
        this.showAssignConfirmDialog(deviceId, userId, name);
      });
    })

  }

  private showAssignConfirmDialog = async (deviceId: string, userId: any, username: any) => {
    const message = `آیا از اتصال کاربر با نام ${username} به این دستگاه اطمینان دارید؟`;
    bootbox.confirm({
      message,
      callback: async (result) => {
        if (!result) return;
        await this.deviceService.updateUser(deviceId, { userId });
        this.componentDidMount();
      }
    });
  }

}

export default withRouter(DevicesTable);
